// Phase 11 — "Copy as" dropdown exposing every Phase-3 format.
// Uses the existing shadcn DropdownMenu primitive.

import { useState } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { allCopyFormats } from "@/lib/symbol-formats";
import { copyText } from "@/lib/copy";

interface Props {
  symbol: string;
  /** Triggers the menu — defaults to a "Copy as ▾" button. */
  className?: string;
}

export function CopyAsMenu({ symbol, className }: Props) {
  const formats = allCopyFormats(symbol);
  const [flash, setFlash] = useState<string | null>(null);

  const copyFormat = async (key: string, value: string) => {
    const ok = await copyText(value, { source: "copy-as", symbol, formatKey: key });
    if (ok) {
      setFlash(key);
      window.setTimeout(() => setFlash((k) => (k === key ? null : k)), 1200);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={
            className ??
            "inline-flex items-center justify-center gap-1 rounded-md border border-border bg-surface/60 px-4 py-2.5 text-sm font-semibold hover:bg-secondary"
          }
          aria-label={`Copy ${symbol} in another format`}
        >
          Copy as
          <span aria-hidden="true" className="text-muted-foreground">▾</span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-72">
        <DropdownMenuLabel>Copy {symbol} as…</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {formats.map((f) => (
          <DropdownMenuItem
            key={f.key}
            onSelect={(e) => {
              // Prevent the menu from closing before the copy resolves.
              e.preventDefault();
              void copyFormat(f.key, f.value);
            }}
            className="flex flex-col items-start gap-0.5"
          >
            <div className="flex w-full items-center justify-between gap-2">
              <span className="text-sm font-medium">{f.label}</span>
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
                {flash === f.key ? "Copied" : f.hint}
              </span>
            </div>
            <code className="block w-full truncate font-mono text-xs text-muted-foreground">
              {f.value}
            </code>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
